import React, { useEffect, useMemo, useState } from "react";
import { Sonometer } from "sonometer/types/sonometer.model";
import { SonometerService } from "../../services/sonometer.service";

import { assetBaseUrl } from "./TextPopup";
import { ChampionshipService } from "services/championship.service";

type SonometerWithRank = Sonometer & { rank: number };

export interface PreRankingRow {
  index: number;
  team: string;
  value: number;
  isHighlighted?: boolean;
}

interface PreRankingPopupProps {
  currentRank?: number;
  sonometer?: Sonometer;
}

interface ChampionshipSonometerRankingResponse {
  championshipId: string;
  totalSonometers: number;
  rank1Sonometers: Array<Sonometer & { rank: number }>;
  rank2Sonometers: Array<Sonometer & { rank: number }>;
  rank3Sonometers: Array<Sonometer & { rank: number }>;
  queriedSonometer: Array<Sonometer & { rank: number }>;
}

const sonometerService = new SonometerService();

const PreRankingPopup: React.FC<PreRankingPopupProps> = ({ sonometer }) => {
  const [rankingData, setRankingData] = useState<PreRankingRow[]>([]);
  function applyCompetitionRanking(
    sonometers: Sonometer[]
  ): SonometerWithRank[] {
    // 1. Sort by score descending
    const sorted = [...sonometers].sort((a, b) => b.score - a.score);

    // 2. Assign competition rank ("1224" style)
    let lastScore: number | null = null;
    let lastRank = 0;

    return sorted.map((item, index) => {
      if (lastScore === item.score) {
        // Same score gets the previous rank
        return { ...item, rank: lastRank };
      } else {
        // New score: rank = index + 1
        const newRank = index + 1;
        lastRank = newRank;
        lastScore = item.score;
        return { ...item, rank: newRank };
      }
    });
  }

  useEffect(() => {
    const fetchRanking = async () => {
      if (!sonometer?.championshipId || !sonometer?.id) {
        return;
      }

      try {
        const response: ChampionshipSonometerRankingResponse =
          await sonometerService.calculateChampionshipSonometerRanking({
            championshipId: sonometer.championshipId,
            sonometerId: sonometer.id,
            isPreRanking: true,
          });

        const {
          totalSonometers,
          rank1Sonometers = [],
          rank2Sonometers = [],
          rank3Sonometers = [],
        } = response;
        void totalSonometers;

        let mappedData: PreRankingRow[] = [];

        // Helper function to safely create ranking row
        const createRankingRow = (
          sono: any,
          highlighted: boolean = false
        ): PreRankingRow | null => {
          if (
            !sono ||
            sono.rank == null ||
            !sono.opponentName ||
            sono.score == null
          ) {
            return null;
          }
          return {
            index: sono.rank,
            team: sono.opponentName,
            value: sono.score,
            isHighlighted: false,
          };
        };

        let totalFilteredSonometers = [
          rank1Sonometers[0],
          rank2Sonometers[0],
          rank3Sonometers[0],
        ].filter(Boolean);

        totalFilteredSonometers = applyCompetitionRanking(
          totalFilteredSonometers
        );

        const totalFilteredSonometersLength = totalFilteredSonometers.length;

        if (totalFilteredSonometersLength === 0) {
          mappedData = [];
        }
        if (totalFilteredSonometersLength === 1) {
          const row1 = createRankingRow(totalFilteredSonometers[0], false);
          mappedData = [row1].filter(Boolean) as PreRankingRow[];
        }
        if (totalFilteredSonometersLength === 2) {
          const row1 = createRankingRow(totalFilteredSonometers[0], false);
          const row2 = createRankingRow(totalFilteredSonometers[1], false);
          mappedData = [row1, row2].filter(Boolean) as PreRankingRow[];
        }
        if (totalFilteredSonometersLength > 2) {
          const row1 = createRankingRow(totalFilteredSonometers[0], false);
          const row2 = createRankingRow(totalFilteredSonometers[1], false);
          const row3 = createRankingRow(totalFilteredSonometers[2], false);
          mappedData = [row1, row2, row3].filter(Boolean) as PreRankingRow[];
        }

        setRankingData(mappedData);
      } catch (error) {
        console.error("Error fetching sonometer ranking:", error);
        // Set empty data on error
        setRankingData([]);
      }
    };

    fetchRanking();
  }, [sonometer]);
  const championshipService = useMemo(() => new ChampionshipService(), []);
  useEffect(() => {
    const fetchChampionship = async () => {
      if (sonometer?.championshipId) {
        try {
          const championshipData =
            await championshipService.fetchChampionshipData(
              sonometer.championshipId
            );
          if (championshipData && championshipData.maxScore) {
            localStorage.setItem(
              `championshipMaxScore-${sonometer.championshipId}`,
              championshipData.maxScore?.toString()
            );
          }
        } catch (error) {
          console.error("Error fetching championship data:", error);
        }
      }
    };

    fetchChampionship();
  }, [sonometer, championshipService]);

  return (
    <div className="w-[1540px] h-[220px] overflow-hidden font-virage">
      <div className="flex h-full">
        {/* Left Sponsor */}
        <div className="w-[260px] h-[220px] flex items-center justify-center">
          <img
            src={
              sonometer?.publicScreenSettings.sponsorLogo ||
              assetBaseUrl + "/images/sponsor_left.png"
            }
            alt="Sponsor"
            className="max-w-full max-h-full"
          />
        </div>

        {/* Margin */}
        <div className="w-[200px]"></div>

        {/* Ranking Section */}
        <div className="w-[620px] flex flex-col justify-start items-center">
          <div className="h-[23px]"></div>

          {rankingData.length > 0 ? (
            rankingData.map((row) => (
              <div
                key={row.index}
                className={`w-[620px] h-[58px] mx-[5px] flex flex-row justify-center items-center rounded-[10px] ${
                  row.isHighlighted ? "border-[3px] border-psg-red" : ""
                }`}
              >
                <div className="w-[80px] h-auto text-right text-[36px]">
                  {row.index} -
                </div>

                <div className="w-[380px] h-auto text-left text-[36px] pl-[7px] overflow-hidden text-ellipsis whitespace-nowrap">
                  PSG vs {row.team}
                </div>

                <div className="flex justify-end items-center w-[140px] h-auto text-right text-[30px]">
                  {row.value.toFixed(1)} dB
                </div>
              </div>
            ))
          ) : (
            // Fallback when no ranking data is available
            <div className="w-[590px] h-[58px] mx-[5px] flex flex-row justify-center items-center text-[36px]">
              Pas de classement pour le moment
            </div>
          )}

          <div className="h-[23px]"></div>
        </div>

        {/* Right Section */}
        <div
          className="h-[220px] w-[460px] flex items-center justify-center text-[90px]"
          style={{
            color: sonometer?.publicScreenSettings?.textColor || "#FFFFFF",
          }}
        ></div>
      </div>
    </div>
  );
};

export default PreRankingPopup;
